import { useEffect, useState, type FC } from 'react';
import { Text, Space, Select } from '@g2u/ui-kit';
import { Form, Popconfirm, Skeleton, Switch } from 'antd';

import { Condition, Customer, JsonData, PaymentMethod, type WithOptionalDataTest } from 'types';
import { CustomerSearchModal } from 'components';
import { PaymentSteps, useServicePayment } from '../../hooks';
import styles from './method.module.scss';
import { useJob } from 'helpers';

type Props = {
  jobId?: string;
  step: PaymentSteps;
  hasCard?: boolean;
  hasDebt: boolean;
  paymentMethod?: PaymentMethod;
  paymentMethods?: JsonData;
  isLoading: boolean;
  updateThirdParty: (id: number) => void;
  isLocked: boolean;
} & WithOptionalDataTest;

export const Method: FC<Props> = (props) => {
  const { jobId, paymentMethod, paymentMethods, isLoading, hasCard, step, hasDebt, updateThirdParty, isLocked } = props;
  const { job } = useJob(jobId);
  const [showSearchCustomerModal, setShowSearchCustomerModal] = useState<boolean>(false);
  const [thirdPartyId, setThirdPartyId] = useState<number | undefined>(job?.billto_ID);
  const [thirdPartyName, setThirdPartyName] = useState<string | undefined>(job?.billto_name);
  const [thirdPartyInvoiceEnabled, setThirdPartyInvoiceEnabled] = useState<boolean>(
    job?.billto_ID !== job?.customer.ID,
  );
  const form = Form.useFormInstance();
  Form.useWatch([], form);
  const { PaymentSteps } = useServicePayment(jobId);
  const useExistingCard = Form.useWatch('useCardOnFile', form) === Condition.Yes;
  const isPaid = step === PaymentSteps.Paid && !hasDebt;

  useEffect(() => {
    setThirdPartyId(job?.billto_ID);
    setThirdPartyName(job?.billto_name);
    form.setFieldValue('thirdPartyId', job?.billto_ID.toString());
  }, [job]);

  useEffect(() => {
    form.setFieldValue('useCardOnFile', hasCard ? Condition.Yes : Condition.No);
  }, [hasCard]);

  const handleConfirm = async (value: boolean) => {
    setThirdPartyInvoiceEnabled(value);
    if (!value && job?.customer.ID) {
      // reset third party to default billto
      job?.billto_ID !== job?.customer.ID && updateThirdParty(job?.customer.ID);
    }
  };

  useEffect(() => {
    form.setFieldValue('paymentMethod', paymentMethod);
  }, [paymentMethod]);

  return (
    <>
      <Text.$ strong size={Text.Size.LG} className={styles.method__title}>
        Payment Method
      </Text.$>
      {isLoading ? (
        <Skeleton />
      ) : (
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle} align={Space.Align.Start}>
          <Text.$>Invoice to the 3rd party</Text.$>

          {thirdPartyInvoiceEnabled ? (
            <>
              <Popconfirm
                title="Remove the 3rd-party Payment"
                description="Are you sure you want to remove the third-party payment? This will delete the connection to the third-party customer."
                onConfirm={() => handleConfirm(false)}
              >
                <Switch
                  onClick={(value) => value !== thirdPartyInvoiceEnabled && undefined}
                  checked={thirdPartyInvoiceEnabled}
                  disabled={isPaid || isLocked}
                />
              </Popconfirm>
              <div onClick={() => setShowSearchCustomerModal(true)} className={styles.method__thirdParty}>
                <Select.$
                  required
                  label="Invoice to:"
                  rules={[{ required: true, message: 'Please select' }]}
                  name="thirdPartyId"
                  options={[{ label: `#${thirdPartyId} ${thirdPartyName}`, value: thirdPartyId?.toString() }]}
                  initialValue={thirdPartyId?.toString()}
                  readonly
                  width={323}
                  disabled={isPaid}
                />
              </div>
              <CustomerSearchModal
                customer={null}
                isOpen={showSearchCustomerModal}
                modalTitle={'Search Customer Files'}
                actions={[
                  {
                    title: 'Select',
                    action: (record: Customer) => {
                      setShowSearchCustomerModal(false);
                      setThirdPartyId(record.ID);
                      setThirdPartyName(record.name.full_name);
                      updateThirdParty(record.ID);
                    },
                  },
                ]}
                onOk={() => setShowSearchCustomerModal(false)}
                onCancel={() => setShowSearchCustomerModal(false)}
                emptyContent={() => null}
              />
            </>
          ) : (
            <>
              <Switch
                onClick={() => handleConfirm(true)}
                checked={thirdPartyInvoiceEnabled}
                disabled={isPaid || isLocked}
              />
            </>
          )}
          <Select.$
            preserve
            required
            label="Pay by credit Card on File:"
            rules={[{ required: true, message: 'Please select' }]}
            name="useCardOnFile"
            options={[
              { label: 'Yes', value: Condition.Yes },
              { label: 'No', value: Condition.No },
            ]}
            disabled={!hasCard || isPaid || isLocked}
            initialValue={hasCard ? Condition.Yes : Condition.No}
            width={113}
          />
          {!useExistingCard && (
            <Select.$
              disabled={useExistingCard || isPaid || isLocked}
              required
              label="Payment Method:"
              rules={[{ required: true, message: 'Please select' }]}
              name="paymentMethod"
              options={paymentMethods?.map((method) => {
                return { value: method.ID, label: method.name };
              })}
              value={useExistingCard ? PaymentMethod.CreditCard : paymentMethod}
              initialValue={useExistingCard ? PaymentMethod.CreditCard : paymentMethod}
              width={300}
            />
          )}
        </Space.$>
      )}
    </>
  );
};
